import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import RecordingService from '../services/recordingService';

export const useRecordingActions = () => {
  const [purchasingId, setPurchasingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const handlePurchase = useCallback(async (recordingId, discountCode = null) => {
    setPurchasingId(recordingId);
    
    try {
      console.log('🔍 useRecordingActions - Purchasing recording:', recordingId);
      const result = await RecordingService.purchaseRecording(recordingId, discountCode);
      console.log('🔍 useRecordingActions - Purchase result:', result);
      
      if (result.success) {
        if (result.isFreeGrant) {
          toast.success(result.message);
          // Refresh the recordings list
          window.location.reload();
        } else if (result.checkoutUrl) {
          // Redirect to Stripe checkout
          window.location.href = result.checkoutUrl;
        }
      } else {
        toast.error(result.error || 'Failed to initiate purchase');
      }
    } catch (error) {
      console.error('🔍 useRecordingActions - Purchase error:', error);
      toast.error('Failed to initiate purchase');
    } finally {
      setPurchasingId(null);
    }
  }, []);

  const handleWatch = useCallback(async (recording) => {
    try {
      console.log('🔍 useRecordingActions - Watching recording:', recording);
      
      // Check if recording has presigned URL
      if (recording.presignedUrl) {
        console.log('🔍 useRecordingActions - Using existing presigned URL');
        window.open(recording.presignedUrl, '_blank');
        return;
      }
      
      // Fallback to getting recording URL
      console.log('🔍 useRecordingActions - Getting recording URL');
      const recordingUrl = await RecordingService.getRecordingUrl(recording.id);
      window.open(recordingUrl, '_blank');
    } catch (error) {
      console.error('🔍 useRecordingActions - Watch error:', error);
      toast.error('Failed to open recording');
    }
  }, []);

  const handleDownload = useCallback(async (recordingId, recordingTitle) => {
    setDownloadingId(recordingId);
    
    try {
      console.log('🔍 useRecordingActions - Downloading recording:', recordingId);
      await RecordingService.downloadRecording(recordingId, recordingTitle);
      toast.success('Download started');
    } catch (error) {
      console.error('🔍 useRecordingActions - Download error:', error);
      toast.error('Failed to download recording');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const handleRename = useCallback(async (recordingId, newTitle) => {
    try {
      console.log('🔍 useRecordingActions - Renaming recording:', recordingId, 'to:', newTitle);
      await RecordingService.renameRecording(recordingId, newTitle);
      toast.success('Recording renamed successfully');
      return true;
    } catch (error) {
      console.error('🔍 useRecordingActions - Rename error:', error);
      toast.error('Failed to rename recording');
      return false;
    }
  }, []);

  const handleDelete = useCallback(async (recordingId) => {
    setDeletingId(recordingId);
    
    try {
      console.log('🔍 useRecordingActions - Deleting recording:', recordingId);
      await RecordingService.deleteRecording(recordingId);
      toast.success('Recording deleted successfully');
      return true;
    } catch (error) {
      console.error('🔍 useRecordingActions - Delete error:', error);
      toast.error('Failed to delete recording');
      return false;
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleTrimCrop = useCallback(async (recordingId, trimCropData) => {
    setProcessingId(recordingId);
    
    try {
      console.log('🔍 useRecordingActions - Starting trim/crop for recording:', recordingId);
      const trackingId = await RecordingService.trimCropRecording(recordingId, trimCropData);
      console.log('🔍 useRecordingActions - Trim/crop tracking ID:', trackingId);
      
      toast.success('Trim/crop operation started');
      return trackingId;
    } catch (error) {
      console.error('🔍 useRecordingActions - Trim/crop error:', error);
      toast.error('Failed to start trim/crop operation');
      return null;
    } finally {
      setProcessingId(null);
    }
  }, []);

  const monitorProcessingStatus = useCallback(async (trackingId, onStatusUpdate) => {
    try {
      console.log('🔍 useRecordingActions - Monitoring processing status:', trackingId);
      const status = await RecordingService.getProcessingStatus(trackingId);
      console.log('🔍 useRecordingActions - Processing status:', status);
      
      if (onStatusUpdate) {
        onStatusUpdate(status);
      }
      
      return status;
    } catch (error) {
      console.error('🔍 useRecordingActions - Status monitoring error:', error);
      return null;
    }
  }, []);

  return {
    purchasingId,
    downloadingId,
    deletingId,
    processingId,
    handlePurchase,
    handleWatch,
    handleDownload,
    handleRename,
    handleDelete,
    handleTrimCrop,
    monitorProcessingStatus
  };
};